import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authdApiRequest } from '../utils/api';
import Layout from '../components/Layout';

function UserForm() {
  const { id } = useParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [level, setLevel] = useState(1);
  const [teams, setTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await authdApiRequest(`${process.env.REACT_APP_API_HOST}/teams`);
        const data = await response.json();
        setTeams(data);
      } catch (error) {
        toast.error('Failed to load teams.');
      }
    };

    const fetchUser = async () => {
      if (id) {
        try {
          const response = await authdApiRequest(`${process.env.REACT_APP_API_HOST}/users/${id}`);
          const userData = await response.json();
          setName(userData.name || '');
          setEmail(userData.email || '');
          setIsAdmin(!!userData.isAdmin);
          setLevel(userData.level || 1);
          
          const teamSelections = {};
          if (userData.Teams?.length) {
            userData.Teams.forEach(team => {
              teamSelections[team.id] = {
                selected: true,
                role: team.UserTeam?.role || 'member'
              };
            });
          }
          setSelectedTeams(teamSelections);
        } catch (error) {
          toast.error('Failed to load user data.');
        }
      }
    };

    fetchTeams();
    fetchUser();
  }, [id]);

  useEffect(() => {
    document.title = id ? `Edit User - ${name} - DevWing` : 'Add New User - DevWing';
  }, [id, name]);

  const handleTeamChange = (teamId) => {
    setSelectedTeams((prev) => ({
      ...prev,
      [teamId]: {
        selected: !prev[teamId]?.selected,
        role: prev[teamId]?.role || 'member'
      },
    }));
  };

  const handleTeamRoleChange = (teamId, role) => {
    setSelectedTeams((prev) => ({
      ...prev,
      [teamId]: {
        selected: true,
        role
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const teamsData = Object.entries(selectedTeams)
      .filter(([_, value]) => value.selected)
      .map(([id, value]) => ({
        id: parseInt(id),
        role: value.role
      }));

    const userData = {
      name,
      email,
      isAdmin,
      level,
      teams: teamsData,
      ...(password && { password })
    };

    const method = id ? 'PUT' : 'POST';
    const url = id ? `${process.env.REACT_APP_API_HOST}/users/${id}` : `${process.env.REACT_APP_API_HOST}/users`;

    try {
      const response = await authdApiRequest(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save user');
      }
      
      toast.success(`User ${id ? 'updated' : 'created'} successfully!`);
      navigate('/users');
    } catch (error) {
      toast.error(error.message || 'Failed to save user');
    }
  };

  return (
    <Layout>
      <h1 className="text-2xl mb-8 font-bold">{id ? 'Edit User' : 'Add New User'}</h1>
      <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
        <input
          type="text"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="p-2 border"
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="p-2 border"
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="p-2 border"
        />
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={isAdmin}
            onChange={() => setIsAdmin(!isAdmin)}
            className="mr-2"
          />
          Site Administrator
        </label>
        <label className="flex items-center">
          <span className="mr-2">Level:</span>
          <select
            value={level}
            onChange={(e) => setLevel(Number(e.target.value))}
            className="p-2 border"
          >
            <option key={1} value={1}>Intern</option>
            <option key={2} value={2}>SE1</option>
            <option key={3} value={3}>SE2</option>
            <option key={4} value={4}>SE3</option>
            <option key={50} value={50}>Staff Engineer</option>
            <option key={60} value={60}>Associate Manager</option>
            <option key={65} value={65}>Manager</option>
            <option key={70} value={70}>Director</option>
            <option key={80} value={80}>VP</option>
            <option key={90} value={90}>C-Suite</option>
          </select>
        </label>
        <div>
          <h2 className="font-bold">Teams</h2>
          {teams.map((team) => (
            <div key={team.id} className="flex items-center space-x-2 mb-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedTeams[team.id]?.selected || false}
                  onChange={() => handleTeamChange(team.id)}
                  className="mr-2"
                />
                {team.name}
              </label>
              {selectedTeams[team.id]?.selected && (
                <select
                  value={selectedTeams[team.id]?.role || 'member'}
                  onChange={(e) => handleTeamRoleChange(team.id, e.target.value)}
                  className="p-1 border rounded"
                >
                  <option value="member">Member</option>
                  <option value="lead">Lead</option>
                </select>
              )}
            </div>
          ))}
        </div>
        <button type="submit" className="p-2 bg-sky-500 text-white rounded-md">
          {id ? 'Update User' : 'Add User'}
        </button>
      </form>
    </Layout>
  );
}

export default UserForm;
